import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment, {Moment} from 'moment';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {EntityShift} from '../../../backend/interfaces/entity-shift';
import {Pallete} from '../../../theme/bluebird';
import {EntityShiftsAllocationsTableRow} from '../../../backend/interfaces/entity-shifts-allocations-table';
import {translations} from '../../../i18n/translations';
import ShiftAllocationSelectInput from '../shifts-allocations-input/shift-allocation-select-input';
import ShiftAllocationPreview from '../shift-allocation-preview/shift-allocation-preview';
import {ComboActions} from '../../combo-actions/combo-actions';
import {FiEdit} from 'react-icons/fi';
import {isFirefox} from '../../../utils/functions/user-agent';
import {UserInfo} from '../../../backend/interfaces/user-info';

interface ShiftsAllocationsTableProps {
  canWriteAllocations: boolean;
  startDate: Moment;
  endDate: Moment;
  shifts?: EntityShift[];
  rows?: EntityShiftsAllocationsTableRow[];
  isLoading: boolean;
  isExporting?: boolean;
  onShiftAllocationChanged: (
    userId: string,
    allocationDay: string,
    entityShiftId: string,
    toDelete: boolean
  ) => void;
}

export type ShiftAllocation = {
  day: string;
  entityShiftIds: string[];
};

type ShiftsAllocationsTableRow = {
  user: UserInfo;
  allocations: ShiftAllocation[];
};

export type ShiftMembers = {
  [id: string]: {[day: string]: number};
};

const SkeletonRows = ({daysCount, rowsCount}: {daysCount: number; rowsCount: number}) => (
  <>
    {new Array(rowsCount).fill(1).map((_item, rowIndex) => (
      <TableRow key={`line_skeleton_${rowIndex}`}>
        {new Array(daysCount + 1).fill(1).map((_item, columnIndex) => (
          <TableCell key={`cell_skeleton_${columnIndex}`}>
            {columnIndex === 0 ? (
              <Skeleton animation="wave" sx={{fontSize: '24px'}} />
            ) : (
              <Skeleton animation="wave" variant="rounded" height={44} sx={{minWidth: '138px'}} />
            )}
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const ShiftsAllocationsTable = ({
  canWriteAllocations,
  startDate,
  endDate,
  shifts,
  rows,
  isLoading: isLoadingRows,
  isExporting = false,
  onShiftAllocationChanged,
}: ShiftsAllocationsTableProps) => {
  const {t} = useTranslation();
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [editMode, setEditMode] = useState(canWriteAllocations);
  const [transformedRows, setTransformedRows] = useState<ShiftsAllocationsTableRow[] | undefined>();
  const [shiftMembers, setShiftMembers] = useState<ShiftMembers>({});
  const diffDays = endDate.diff(startDate, 'days');
  const selectedWeekDays = Array.from(Array(diffDays), (_, index) =>
    startDate.clone().add(index, 'days')
  );
  const effectiveEditMode = isExporting ? false : editMode;
  const isLoading = isLoadingRows || !rows || !transformedRows || !shifts;

  useEffect(() => {
    if (!rows) {
      setShiftMembers({});
      setTransformedRows(undefined);
      return;
    }

    const transformed: ShiftsAllocationsTableRow[] = [];
    const members: ShiftMembers = {};
    rows.forEach((row) => {
      const allocations = row.allocations.reduce((accumulator: ShiftAllocation[], allocation) => {
        const found = accumulator.find((a) => a.day === allocation.day);
        if (allocation.entityShiftId) {
          if (!members[allocation.entityShiftId]) {
            members[allocation.entityShiftId] = {[allocation.day]: 1};
          } else {
            members[allocation.entityShiftId][allocation.day] = members[allocation.entityShiftId][
              allocation.day
            ]
              ? members[allocation.entityShiftId][allocation.day] + 1
              : 1;
          }

          if (found?.entityShiftIds) {
            found.entityShiftIds.push(allocation.entityShiftId);
          } else {
            accumulator.push({
              day: allocation.day,
              entityShiftIds: [allocation.entityShiftId],
            });
          }
        } else {
          accumulator.push({
            day: allocation.day,
            entityShiftIds: [],
          });
        }

        return accumulator;
      }, []);
      transformed.push({user: row.user, allocations: allocations});
    });

    setShiftMembers(members);
    setTransformedRows(transformed);
  }, [rows]);

  const tableContainerStyle: SxProps = {
    // Card-like styles
    borderRadius: '5px',
    border: effectiveEditMode ? '1px solid var(--dcm-blue-light)' : 'none',
    background: 'var(--dcm-white-base)',
    boxShadow: '0px 4px 16px 0px rgba(29, 41, 57, 0.15)',

    // Padding must be added to the table + the actions wrapper div instead. Otherwise rows would display behind the
    // sticky table header on top
    padding: 0,

    // Positioning to handle table scroll/responsive behavior
    position: 'absolute',
    overflow: 'auto',

    width: isFirefox ? '-moz-available' : '-webkit-fill-available',
    maxHeight: isFirefox ? '70%' : '-webkit-fill-available',
    minHeight: '-webkit-fill-available',

    marginTop: '16px',
    marginRight: '16px',

    // scrollbar
    scrollbarWidth: 'thin',
    scrollbarHeight: 'thin',
    '&::-webkit-scrollbar': {
      width: '0.4em',
      height: '0.4em',
    },
  };

  const editModeActionsContainerStyle: SxProps = {
    position: 'sticky',
    top: 0,
    left: 0,
    // Padding needs to be applied on the actions wrapper div rather than the table container
    padding: '16px 16px 0',
    backgroundColor: Pallete.white.base,
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 10,
  };

  const tableStyle: SxProps = {
    margin: '16px',
    width: 'calc(100% - 32px)',
    borderCollapse: 'collapse',
    '& .MuiTableCell-root': {border: 'none'},
    '&.shifts-allocations-table-pdf tbody tr': {
      borderBottom: 0,
      borderTop: 0,
    },
    '&.shifts-allocations-table-pdf thead th, &.shifts-allocations-table-pdf tbody td': {
      border: 0,
      borderTop: '1px solid #e0e0e0',
      borderLeft: '1px solid #e0e0e0',
    },
    '&.shifts-allocations-table-pdf thead th:first-child, &.shifts-allocations-table-pdf tbody td:first-child':
      {
        paddingLeft: '12px',
      },
    '&.shifts-allocations-table-pdf thead th:last-child, &.shifts-allocations-table-pdf tbody td:last-child':
      {
        borderRight: '1px solid #e0e0e0',
        paddingRight: '7px',
      },
    '&.shifts-allocations-table-pdf tbody tr:last-child td': {
      borderBottom: '1px solid #e0e0e0',
    },
  };

  const headerCellStyle: SxProps = {
    verticalAlign: 'middle',
    // Padding needs to be applied on the table div rather than the table container
    paddingTop: '24px',
    backgroundColor: Pallete.white.base,
    top: canWriteAllocations ? '48px' : '0px',
  };

  const EditModeActions = () => (
    <Box sx={editModeActionsContainerStyle}>
      {editMode ? (
        <Button
          color="error"
          onClick={() => setEditMode(false)}
          sx={{
            color: 'var(--dcm-red-base)',
            letterSpacing: '0.175px',
            lineHeight: '20px',
            textTransform: 'unset',
          }}
        >
          {t(translations.office.professional_caregiver.shifts_allocations.exit_edit_mode)}
        </Button>
      ) : (
        <ComboActions
          className="actions"
          wrapperSX={{
            color: Pallete.gray.light,
          }}
          triggerSX={{
            height: '32px',
            width: '32px',
            '&:active': {backgroundColor: Pallete.blue.lighter},
          }}
          actions={[
            {
              label: t(translations.user_card.actions.edit),
              icon: {element: FiEdit},
              callback: () => setEditMode(true),
            },
          ]}
        />
      )}
    </Box>
  );

  return (
    <TableContainer component={Paper} sx={tableContainerStyle}>
      {canWriteAllocations && !isExporting && <EditModeActions />}
      <Table
        stickyHeader
        className={isExporting ? 'shifts-allocations-table-pdf' : undefined}
        sx={tableStyle}
      >
        <TableHead>
          <TableRow ref={rowRef}>
            <TableCell
              key={`head_cell_collaborators`}
              sx={{...headerCellStyle, paddingX: 0, width: '118px'}}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '24px',
                  color: 'var(--dcm-gray)',
                }}
              >
                {t(
                  translations.office.professional_caregiver.shifts_allocations.table.collaborators
                )}
              </Typography>
            </TableCell>
            {selectedWeekDays.map((date) => (
              <TableCell align="center" sx={headerCellStyle} key={`head_cell_day_${date.day()}`}>
                {isLoading ? (
                  <Stack flexDirection="column" alignItems="center">
                    <Skeleton variant="text" width="30px" sx={{fontSize: '24px'}} />
                    <Skeleton variant="text" width="20px" sx={{fontSize: '16px'}} />
                  </Stack>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '24px',
                        fontWeight: 500,
                        lineHeight: '24px',
                        color: 'var(--dcm-gray)',
                      }}
                    >
                      {date.format('DD')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '24px',
                        color: 'var(--dcm-gray)',
                        textTransform:
                          date.isSame(moment(), 'day') && !isExporting ? 'uppercase' : 'none',
                      }}
                    >
                      {date.isSame(moment(), 'day') && !isExporting
                        ? t(
                            translations.office.professional_caregiver.shifts_allocations.table
                              .today
                          )
                        : date.format('ddd')}
                    </Typography>
                  </>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            '& .MuiTableCell-root': {
              padding: '8px 6px',
              '&:first-of-type': {paddingLeft: 0},
              '&:last-of-type': {paddingRight: 0},
            },
          }}
        >
          {isLoading ? (
            <SkeletonRows daysCount={diffDays} rowsCount={8} />
          ) : (
            transformedRows.map((row) => (
              <TableRow
                key={`line_user_${row.user.id}`}
                sx={{
                  borderTop: '0.3px solid var(--dcm-gray-lightbg)',
                  borderBottom: '0.3px solid var(--dcm-gray-lightbg)',
                }}
              >
                <TableCell>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: 'var(--dcm-blue-base)',
                    }}
                  >
                    {row.user.name}
                  </Typography>
                </TableCell>
                {row.allocations.map((allocation) => {
                  const dayShifts: EntityShift[] = [];
                  shifts.forEach((shift) => {
                    if (allocation.entityShiftIds.includes(shift.id!)) {
                      dayShifts.push(shift);
                    }
                  });
                  // collaborator cell width = 118
                  const total = rowRef.current !== null ? rowRef.current.clientWidth - 118 : 0;
                  // 7 week days. 12px padding between each cell
                  const cellWidth = total / 7 - 12;
                  // forcing the width to avoid the width jumping
                  return (
                    <TableCell key={`allocation_${allocation.day}_${row.user.id}}`}>
                      {effectiveEditMode ? (
                        <ShiftAllocationSelectInput
                          allocation={allocation}
                          shifts={shifts}
                          userId={row.user.id}
                          cellWidth={cellWidth}
                          onChange={(shiftId, toDelete) => {
                            onShiftAllocationChanged(
                              row.user.id,
                              allocation.day,
                              shiftId,
                              toDelete
                            );
                          }}
                        />
                      ) : (
                        <ShiftAllocationPreview
                          day={allocation.day}
                          userId={row.user.id}
                          shifts={dayShifts}
                          shiftMembers={shiftMembers}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ShiftsAllocationsTable;
